import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Repeat, PenTool, Mail, Linkedin, Phone, Zap } from 'lucide-react';

const CHANNEL_ICONS = { letter: PenTool, email: Mail, linkedin: Linkedin, call: Phone };
const CHANNEL_BARS = { letter: 'bg-amber-600', email: 'bg-sky-700', linkedin: 'bg-blue-600', call: 'bg-emerald-600' };

// "40% reply after the letter, 25% only after LinkedIn" — response attribution per channel
export function SequencePerformanceCard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.getSequenceStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats || stats.total === 0) return null;
  const maxRate = Math.max(1, ...stats.channels.map(c => c.rate));

  return (
    <div className="bg-card border border-border rounded-sm p-5" data-testid="sequence-performance-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Repeat className="w-4 h-4 text-violet-400" />
        <h2 className="text-sm font-medium text-foreground">Sequence performance</h2>
        <span className="text-[10px] text-muted-foreground">which step gets owners to reply</span>
        <span className="ml-auto text-[10px] font-mono text-muted-foreground">
          {stats.letters_sent ?? stats.eligible} letters sent · {stats.total} in sequences · {stats.responses} responses
          {(stats.letters_sent ?? stats.eligible) > 0 && <> · <span className="text-emerald-400">{stats.response_rate}% response rate</span></>}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {stats.channels.map(c => {
          const Icon = CHANNEL_ICONS[c.channel] || Mail;
          return (
            <div key={c.channel} data-testid={`seq-perf-${c.channel}`}>
              <div className="flex items-center gap-2 text-xs text-foreground">
                <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                {c.label}
                <span className="ml-auto font-mono text-muted-foreground">{c.responses}/{c.executed}</span>
              </div>
              <div className="h-1.5 bg-secondary/40 rounded-full mt-1.5 overflow-hidden">
                <div className={`h-full rounded-full ${CHANNEL_BARS[c.channel]}`}
                  style={{ width: `${Math.min(100, (c.rate / maxRate) * 100)}%` }} />
              </div>
              <p className="text-[9px] text-muted-foreground/60 mt-0.5">
                {c.channel === 'letter'
                  ? (c.executed === 0 ? 'no letters sent yet' : `${c.rate}% of all sent letters got a response`)
                  : (c.executed === 0 ? 'no steps executed yet' : `${c.rate}% replied after this step (${c.executed} executed)`)}
              </p>
            </div>
          );
        })}
      </div>
      {stats.responses_before_first > 0 && (
        <p className="flex items-center gap-1.5 text-[10px] text-muted-foreground mt-3">
          <Zap className="w-3 h-3 text-amber-400" /> {stats.responses_before_first} replied before the first step was executed.
        </p>
      )}
    </div>
  );
}
